package com.macrofocus.high_d.tablelens

import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.license.LicenseModel
import com.macrofocus.order.MutableVisibleOrder
import org.mkui.component.absolute.AbsoluteCPContainer
import org.mkui.component.absolute.Layout
import com.macrofocus.common.command.Command
import org.mkui.component.button.CPButton
import org.mkui.geom.Rectangle2D
import kotlin.math.max

class DefaultTableLensView<Row,Column> : AbstractTableLensView<Row,Column>() {
    override val component: AbsoluteCPContainer = AbsoluteCPContainer(object : Layout {
        override fun layout(container: AbsoluteCPContainer, width: Double, height: Double) {
            runLayout()
        }
    })

    private val axisLabels: MutableMap<AxisModel<Row,Column>, CPButton> = HashMap<AxisModel<Row,Column>, CPButton>()
    override var model: TableLensModel<Row, Column>? = null
        get() = field
        set(value) {
            field = value
            tableLensComponent.model = value
        }
    private val tableLensComponent: TableLensComponent<Row, Column> = DefaultTableLensComponent(this)
    private var isSelectionMode = true

    init {
        component.addComponent(tableLensComponent)
    }

    override fun createOverplots() {
        if (tableLensComponent != null) {
            tableLensComponent.createOverplots()
            tableLensComponent.scheduleUpdate()
        }
    }

    override fun refresh() {
        runLayout()
    }

    private fun runLayout() {
        if(model != null && getWidth() > 0 && getHeight() > 0) {
            var headerMaximumHeight = 0.0
            val axisOrder: MutableVisibleOrder<AxisModel<Row, Column>>? = model!!.getAxisGroupModel().axisOrder
            for (axisModel: AxisModel<Row, Column> in axisOrder!!.iterableAll()) {
                val header: CPButton = getHeader(axisModel)
                if (axisOrder.isVisible(axisModel)) {
                    val i = axisOrder.indexOf(axisModel)
                    val x1Location: Double? = model?.getLocation(axisModel)
                    var x2Location: Double?
                    if (i + 1 < axisOrder.size()) {
                        x2Location = model?.getLocation((axisOrder.get(i + 1)))
                    } else {
                        x2Location = 1.0
                    }
                    if (x1Location != null && x2Location != null) {
                        val x = (x1Location * getWidth())
                        val y = 0.0
                        val width = (x2Location * getWidth()) - x
                        val height: Double = HEADER_HEIGHT
                        headerMaximumHeight = max(height, headerMaximumHeight)

                        component.setBounds(header, Rectangle2D.Double(x, y, width, height))
                    }
                    component.setVisible(header, true)
                } else {
                    component.setVisible(header, false)
                }
            }
            val bounds = Rectangle2D.Double(0.0, headerMaximumHeight, getWidth().toDouble(), getHeight() - headerMaximumHeight)
            component.setBounds(tableLensComponent, bounds)
        }
    }

    fun getHeader(axisModel: AxisModel<Row,Column>): CPButton {
        if (!axisLabels.containsKey(axisModel)) {
            val label: CPButton = createAxisHeaderView(axisModel)
            //            label.addMouseListener(new MouseAdapter() {
//                @Override
//                public void mousePressed(MouseEvent e) {
//                    model.setOrder(axisModel);
//                }
//
//                @Override
//                public void mouseExited(MouseEvent e) {
//                    nativeComponent.requestFocusInWindow(true);
//                }
//            });
            //        label.setModel(model.getButtonModel(index));
            axisLabels.put(axisModel, label)
            component.addComponent(label, 1)
        }
        return axisLabels.get(axisModel)!!
    }

    /** The header of an axis, a button sorting the rows by that axis as AxisHeaderView of SwingTableLensView. */
    protected fun createAxisHeaderView(axisModel: AxisModel<Row, Column>): CPButton {
        val command = object : Command {
            override fun execute() {
                model?.setOrder(axisModel)
            }
        }
        return CPButton(SimpleProperty<Command?>(command), SimpleProperty(axisModel.name))
    }

    /** The top of the plot of the rows, below the headers. */
    fun plotTop(): Int = plotBounds().y.toInt()

    /** The bounds of the plot of the rows, below the headers. */
    private fun plotBounds(): Rectangle2D = component.getBounds(tableLensComponent) ?: Rectangle2D.Double(0.0, 0.0, 0.0, 0.0)

    override fun getClosestRow(x: Int, y: Int): Row? {
        val b = plotBounds()
        return tableLensComponent.getClosestRow(x - b.x.toInt(), y - b.y.toInt())
    }

    override fun getRows(rect: Rectangle2D?): List<Row> {
        val b = plotBounds()
        return tableLensComponent.getRows(Rectangle2D.Double(rect!!.x - b.x, rect.y - b.y, rect.width, rect.height))
    }

    override fun isSelectionMode(): Boolean {
        return isSelectionMode
    }

    override fun setSelectionMode(value: Boolean) {
        isSelectionMode = value
    }

    override fun getWidth(): Int {
        return component.width.toInt()
    }

    override fun getHeight(): Int {
        return component.height.toInt()
    }

    override fun setLicenseModel(licenseModel: LicenseModel?) {
        TODO("Not yet implemented")
    }

    private companion object {
        /** Height of the headers, as the AxisHeaderView buttons. */
        const val HEADER_HEIGHT = 22.0
    }
}
